import { stegaClean } from '@/lib/utils/stega';
import type { MediaDto } from '@/lib/sanity/dto';

/**
 * Readable form of the asset's file name — extension dropped, separators turned
 * into spaces ('wohnung-39_bad.jpg' → 'wohnung 39 bad'). Last resort for a name:
 * a file name read aloud is poor, but silence is worse.
 */
function resolveFilenameLabel(asset?: MediaDto['asset']): string {
    const filename = stegaClean(asset?.originalFilename || '');

    return filename
        .replace(/\.[^.]+$/, '')
        .replace(/[-_]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

/**
 * Accessible alt text for a Sanity image: asset `altText`, then the media library
 * `title`, then the caller's fallback (which knows the context — a person's name,
 * the block title), and the asset's file name as the last resort. So the result is
 * effectively never empty — set `altText` in Sanity to keep it from degrading to a
 * file name.
 *
 * Purely decorative images must NOT use this helper: pass `alt=""` directly.
 *
 * `alt` is an attribute, not rendered text, so stega encoding is stripped here
 * (see AGENTS.md → Stega encoding).
 */
export function resolveImageAlt(image?: MediaDto | null, fallback = ''): string {
    const asset = image?.asset;
    const authored = stegaClean(asset?.altText || asset?.title || '');

    return authored || stegaClean(fallback) || resolveFilenameLabel(asset);
}

/**
 * Accessible name for a control whose only content is an image (lightbox
 * triggers). The caption wins over the asset metadata: it describes the image in
 * context, and when it is also rendered inside the control the name still
 * contains the visible text (WCAG 2.5.3 "Label in Name"). `fallback` is the last
 * resort behind the whole alt cascade, file name included.
 */
export function resolveImageActionLabel(
    image: MediaDto | undefined | null,
    action: string,
    caption?: string,
    fallback = 'Bild',
): string {
    const cleanCaption = caption ? stegaClean(caption).trim() : '';
    const subject = cleanCaption || resolveImageAlt(image) || fallback;

    return `${subject} ${action}`;
}

/** An SVG has no pixels to resize: the lightbox scales it itself (LightboxRoot). */
export function isSvgImage(image?: MediaDto | null): boolean {
    const asset = image?.asset;

    return asset?.mimeType === 'image/svg+xml' || asset?.extension === 'svg';
}
